import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      ref={ref}
      className="py-20 md:py-32 bg-cream-alt relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-terracotta font-medium uppercase tracking-widest text-sm">
            Contacto
          </span>
          <h2
            className="font-heading text-4xl md:text-5xl font-bold text-coffee mt-4 mb-6"
            data-testid="contact-title"
          >
            ¿Tienes alguna pregunta?
          </h2>
          <p className="text-coffee-light text-lg max-w-2xl mx-auto">
            Estamos aquí para ayudarte. Contáctanos por WhatsApp para una respuesta rápida
            o utiliza cualquiera de nuestros canales de comunicación.
          </p>
        </motion.div>

        {/* Contact Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {/* WhatsApp */}
          <motion.a
            href="https://wa.me/34623513773?text=Hola,%20tengo%20una%20consulta"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 text-center group hover:-translate-y-2"
            data-testid="contact-whatsapp"
          >
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
              <MessageCircle className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-heading font-semibold text-xl text-coffee mb-2">WhatsApp</h3>
            <p className="text-coffee-light mb-4">Respuesta rápida</p>
            <span className="text-terracotta font-medium">+34 623 51 37 73</span>
          </motion.a>

          {/* Phone */}
          <motion.a
            href="tel:+34623513773"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 text-center group hover:-translate-y-2"
            data-testid="contact-phone"
          >
            <div className="w-16 h-16 bg-terracotta rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
              <Phone className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-heading font-semibold text-xl text-coffee mb-2">Teléfono</h3>
            <p className="text-coffee-light mb-4">Llámanos directamente</p>
            <span className="text-terracotta font-medium">+34 623 51 37 73</span>
          </motion.a>

          {/* Email */}
          <motion.a
            href="mailto:salkantayrestaurante@gmail.com"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 text-center group hover:-translate-y-2"
            data-testid="contact-email"
          >
            <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
              <Mail className="w-8 h-8 text-coffee" />
            </div>
            <h3 className="font-heading font-semibold text-xl text-coffee mb-2">Email</h3>
            <p className="text-coffee-light mb-4">Escríbenos</p>
            <span className="text-terracotta font-medium text-sm">salkantayrestaurante@gmail.com</span>
          </motion.a>
        </div>

        {/* Main CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-gradient-to-r from-terracotta to-terracotta-dark rounded-3xl p-8 md:p-12 text-center relative overflow-hidden"
          data-testid="contact-cta"
        >
          {/* Decorative */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="relative">
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Reserva tu Mesa Hoy
            </h3>
            <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">
              Vive la experiencia SALKANTAY. Haz tu reserva por WhatsApp y disfruta 
              de la mejor cocina peruana en Madrid.
            </p>
            <Button
              asChild
              size="lg"
              className="bg-white hover:bg-cream-DEFAULT text-terracotta rounded-full px-10 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-xl"
              data-testid="contact-reserve-btn"
            >
              <a
                href="https://wa.me/34623513773?text=Hola,%20me%20gustaría%20reservar%20una%20mesa"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Reservar por WhatsApp
              </a>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
